// Lib
import { FC, useEffect, useState } from "react";
// Api
import {
  useLazyGetScheduleOrdersSettingsQuery,
  useUpdateScheduleOrdersSettingsMutation,
} from "rtkQuery/query/ordersAPI";
import { useLazyGetAuthSettingsQuery } from "rtkQuery/query/authSettingsAPI";
import { useUpdateCoreSettingsMutation } from "rtkQuery/query/coreSettingsAPI";
// Hooks
import { useNotification, usePermissions } from "hooks";
// Helpers
import { getConfirmModalTitle } from "../../helpers";
// Utils
import { errorHandler } from "utils/errorHandler";
// Icons
import { PersonIcon } from "icons";
// Layouts
// Components
import { ConfirmDialog } from "components/Modals";
import SettingsItem from "../SettingsItem";
import { Switch, Input } from "../Inputs";

// Styled
import { ContentBox } from "styled/Box";
import { Typography } from "styled/Typography";

import {
  ConfirmReferralModalType,
  initialLoadingState,
  initialState,
  LoadingStateValues,
  notificationMessage,
  ScheduleOrder,
  StateValues,
} from "./config";
import { getSettingsName } from "pages/Settings/helpers";

export const ScheduleOrdersSettings: FC = () => {
  const { openNotification } = useNotification();

  const { canScheduleOrderSettingsUpdate } = usePermissions();

  const [fetchAuthSettings] = useLazyGetAuthSettingsQuery();
  const [get] = useLazyGetScheduleOrdersSettingsQuery();
  const [update] = useUpdateScheduleOrdersSettingsMutation();
  const [updateCoreSettings] = useUpdateCoreSettingsMutation();

  const [settings, setSettings] = useState<StateValues>(initialState);

  const [isLoading, setIsLoading] =
    useState<LoadingStateValues>(initialLoadingState);

  const [confirmModal, setConfirmModal] = useState<
    ConfirmReferralModalType | false
  >(false);

  const getAuthSettings = async () => {
    try {
      setIsLoading(prev => ({
        ...prev,
        [ScheduleOrder.CancelTimeAfterStart]: true,
      }));

      const { scheduleOrder } = await fetchAuthSettings().unwrap();

      setSettings(prev => ({
        ...prev,
        [ScheduleOrder.CancelTimeAfterStart]:
          scheduleOrder.cancelTimeAfterStart,
      }));
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({
        ...prev,

        [ScheduleOrder.CancelTimeAfterStart]: false,
      }));
    }
  };

  const getSettings = async () => {
    try {
      setIsLoading(prev => ({
        ...prev,
        [ScheduleOrder.IsScheduleOrders]: true,
        [ScheduleOrder.OrderCancelTime]: true,
        [ScheduleOrder.ScheduleOrderTime]: true,
        [ScheduleOrder.OrderAppearingTime]: true,
        [ScheduleOrder.TimeToNextSlot]: true,
      }));

      const data = await get().unwrap();

      setSettings(prev => ({ ...prev, ...data }));
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({
        ...prev,
        [ScheduleOrder.IsScheduleOrders]: false,
        [ScheduleOrder.OrderCancelTime]: false,
        [ScheduleOrder.ScheduleOrderTime]: false,
        [ScheduleOrder.OrderAppearingTime]: false,
        [ScheduleOrder.TimeToNextSlot]: false,
      }));
    }
  };

  useEffect(() => {
    getSettings();
    getAuthSettings();
  }, []);

  const handleUpdateSettings = async (
    key: ScheduleOrder,
    value: boolean | number,
  ) => {
    const isValueNumber = typeof value === "number";

    try {
      setIsLoading(prev => ({ ...prev, [key]: true }));

      const payload = {
        [ScheduleOrder.IsScheduleOrders]:
          settings[ScheduleOrder.IsScheduleOrders],
        [ScheduleOrder.OrderCancelTime]:
          settings[ScheduleOrder.OrderCancelTime],
        [ScheduleOrder.ScheduleOrderTime]:
          settings[ScheduleOrder.ScheduleOrderTime],
        [ScheduleOrder.OrderAppearingTime]:
          settings[ScheduleOrder.OrderAppearingTime],
        [ScheduleOrder.TimeToNextSlot]: settings[ScheduleOrder.TimeToNextSlot],
        [key]: value,
      };

      if (isValueNumber) {
        await update(payload).unwrap();

        setSettings(prev => ({ ...prev, [key]: value }));
      } else {
        setSettings(prev => ({ ...prev, [key]: value }));

        await update(payload).unwrap();
      }

      openNotification({ message: notificationMessage[key] });
    } catch (error) {
      if (!isValueNumber) {
        setSettings(prev => ({ ...prev, [key]: !value }));
      }

      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({ ...prev, [key]: false }));

      if (confirmModal) {
        setConfirmModal(false);
      }
    }
  };

  const handleUpdateCoreSettings = async (
    key: ScheduleOrder.CancelTimeAfterStart,
    value: number,
  ) => {
    try {
      setIsLoading(prev => ({ ...prev, [key]: true }));

      await updateCoreSettings({ [key]: value }).unwrap();

      setSettings(prev => ({ ...prev, [key]: value }));

      openNotification({ message: notificationMessage[key] });
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleUpdate = (
    key: ScheduleOrder,
    value: StateValues[ScheduleOrder],
  ) => {
    switch (key) {
      case ScheduleOrder.IsScheduleOrders:
        setSettings(prev => ({ ...prev, [key]: value as boolean }));
        setConfirmModal({ key, value });
        return;

      default:
        break;
    }
  };

  const onConfirm = () => {
    if (!confirmModal) return;

    switch (confirmModal.key) {
      case ScheduleOrder.IsScheduleOrders:
        handleUpdateSettings(confirmModal.key, confirmModal.value as boolean);
        return;

      default:
        break;
    }
  };

  const onCancel = () => {
    if (!confirmModal) return;

    setSettings(prev => ({ ...prev, [confirmModal.key]: !confirmModal.value }));

    setConfirmModal(false);
  };

  return (
    <>
      <ContentBox $column $gap={12}>
        <Typography.H2>Orders Schedule</Typography.H2>

        <div>
          <SettingsItem title={getSettingsName(ScheduleOrder.IsScheduleOrders)}>
            <Switch
              isLoading={isLoading[ScheduleOrder.IsScheduleOrders]}
              canUpdate={canScheduleOrderSettingsUpdate}
              data={settings[ScheduleOrder.IsScheduleOrders]}
              onChange={value =>
                handleUpdate(ScheduleOrder.IsScheduleOrders, value)
              }
            />
          </SettingsItem>

          <SettingsItem
            title={getSettingsName(ScheduleOrder.TimeToNextSlot)}
            description="Set a minimum time between order placed time and selected time slot for scheduled orders"
          >
            <Input
              suffix="minutes"
              step={1}
              isLoading={isLoading[ScheduleOrder.TimeToNextSlot]}
              canUpdate={canScheduleOrderSettingsUpdate}
              data={settings[ScheduleOrder.TimeToNextSlot]}
              onSubmit={value =>
                handleUpdateSettings(ScheduleOrder.TimeToNextSlot, value)
              }
            />
          </SettingsItem>

          <SettingsItem
            title={getSettingsName(ScheduleOrder.OrderCancelTime)}
            description="Set a minimum order rejection time before the selected time slot starts"
          >
            <Input
              suffix="minutes"
              step={1}
              isLoading={isLoading[ScheduleOrder.OrderCancelTime]}
              canUpdate={canScheduleOrderSettingsUpdate}
              data={settings[ScheduleOrder.OrderCancelTime]}
              onSubmit={value =>
                handleUpdateSettings(ScheduleOrder.OrderCancelTime, value)
              }
            />
          </SettingsItem>

          <SettingsItem
            title={getSettingsName(ScheduleOrder.CancelTimeAfterStart)}
            description="Set a minimum order rejection time before starting order preparation for scheduled orders"
          >
            <Input
              suffix="minutes"
              step={1}
              isLoading={isLoading[ScheduleOrder.CancelTimeAfterStart]}
              canUpdate={canScheduleOrderSettingsUpdate}
              data={settings[ScheduleOrder.CancelTimeAfterStart]}
              onSubmit={value =>
                handleUpdateCoreSettings(
                  ScheduleOrder.CancelTimeAfterStart,
                  value,
                )
              }
            />
          </SettingsItem>

          <SettingsItem
            title={getSettingsName(ScheduleOrder.ScheduleOrderTime)}
            description="Set the maximum number of days for scheduled orders"
          >
            <Input
              suffix="days"
              step={1}
              isLoading={isLoading[ScheduleOrder.ScheduleOrderTime]}
              canUpdate={canScheduleOrderSettingsUpdate}
              data={settings[ScheduleOrder.ScheduleOrderTime]}
              onSubmit={value =>
                handleUpdateSettings(ScheduleOrder.ScheduleOrderTime, value)
              }
            />
          </SettingsItem>

          <SettingsItem
            title={getSettingsName(ScheduleOrder.OrderAppearingTime)}
            description="Set the time for the scheduled order to appear in the kitchen"
          >
            <Input
              suffix="minutes"
              step={1}
              isLoading={isLoading[ScheduleOrder.OrderAppearingTime]}
              canUpdate={canScheduleOrderSettingsUpdate}
              data={settings[ScheduleOrder.OrderAppearingTime]}
              onSubmit={value =>
                handleUpdateSettings(ScheduleOrder.OrderAppearingTime, value)
              }
            />
          </SettingsItem>
        </div>
      </ContentBox>

      <ConfirmDialog
        isLoading={confirmModal && isLoading[confirmModal.key]}
        open={!!confirmModal}
        Icon={PersonIcon}
        message={
          confirmModal &&
          getConfirmModalTitle({
            key: confirmModal.key,
            value: confirmModal.value,
          })
        }
        description="Are you sure to continue this action?"
        onCancel={onCancel}
        firstCTAButton={{
          title: "Accept",
          status: "danger",
          disabled: false,
          loading: confirmModal && isLoading[confirmModal.key],
          onClick: onConfirm,
        }}
      />
    </>
  );
};
